import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEvent, useCreateContentPage } from '@/lib/api/queries'
import { useState, useEffect, useRef } from 'react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useAuth } from '@/lib/auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.eventpay.com/en/api'

export const Route = createFileRoute('/_authenticated/events/$eventId/event-content/create')({
  component: CreateContentPage,
})

function CreateContentPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const navigate = useNavigate()
  const { token } = useAuth()
  
  // Permission check
  const canEditEventContent = usePermission('can_edit_event_content')
  
  const { data: event } = useEvent(eventIdNum)
  const createMutation = useCreateContentPage()
  
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const editorRef = useRef<HTMLTextAreaElement>(null)
  const editorInitialized = useRef(false)
  
  // Initialize TinyMCE
  useEffect(() => {
    if (editorInitialized.current) return
    
    const initEditor = () => {
      if (typeof window === 'undefined' || !(window as unknown as { tinymce?: unknown }).tinymce) {
        // Load TinyMCE script if not loaded
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js'
        script.referrerPolicy = 'origin'
        script.onload = () => {
          setTimeout(setupTinyMCE, 100)
        }
        document.head.appendChild(script)
      } else {
        setupTinyMCE()
      }
    }
    
    const setupTinyMCE = () => {
      const tinymce = (window as unknown as { tinymce: TinyMCE }).tinymce
      if (!tinymce) return
      
      tinymce.remove()
      
      tinymce.init({
        selector: '#content-editor',
        height: 500,
        menubar: 'file edit view insert format tools table',
        plugins: 'anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount code fullscreen',
        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat | code fullscreen',
        content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; }',
        skin: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oxide-dark' : 'oxide',
        content_css: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'default',
        // Image upload configuration
        images_upload_url: `${API_BASE_URL}/upload-media/`,
        automatic_uploads: true,
        images_file_types: 'jpg,jpeg,png,gif,webp,svg',
        file_picker_types: 'image',
        // Custom upload handler with authentication
        images_upload_handler: (blobInfo: BlobInfo, progress: (percent: number) => void) => {
          return new Promise<string>((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhr.open('POST', `${API_BASE_URL}/upload-media/`)
            
            if (token) {
              xhr.setRequestHeader('Authorization', `Token ${token}`)
            }
            
            xhr.upload.onprogress = (e) => {
              progress((e.loaded / e.total) * 100)
            }
            
            xhr.onload = () => {
              if (xhr.status === 403) {
                reject({ message: 'Authentication required', remove: true })
                return
              }
              
              if (xhr.status < 200 || xhr.status >= 300) {
                reject('HTTP Error: ' + xhr.status)
                return
              }
              
              const json = JSON.parse(xhr.responseText)
              if (!json || typeof json.location !== 'string') {
                reject('Invalid response: ' + xhr.responseText)
                return
              }
              
              resolve(json.location)
            }
            
            xhr.onerror = () => {
              reject('Image upload failed. Code: ' + xhr.status)
            }
            
            const formData = new FormData()
            formData.append('file', blobInfo.blob(), blobInfo.filename())
            xhr.send(formData)
          })
        },
        setup: (editor: TinyMCEEditor) => {
          editor.on('change', () => {
            setContent(editor.getContent())
          })
          editor.on('blur', () => {
            setContent(editor.getContent())
          })
        },
      })
      
      editorInitialized.current = true
    }
    
    initEditor()
    
    return () => {
      const tinymce = (window as unknown as { tinymce?: TinyMCE }).tinymce
      if (tinymce) {
        tinymce.remove()
      }
      editorInitialized.current = false
    }
  }, [])
  
  // Permission guard
  if (!canEditEventContent) {
    return <AccessDenied message="You don't have permission to create content pages." />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    // Get content from TinyMCE
    const tinymce = (window as unknown as { tinymce?: TinyMCE }).tinymce
    const finalContent = tinymce?.activeEditor?.getContent() || content
    
    if (!title.trim()) {
      setError('Title is required.')
      return
    }
    
    if (!finalContent.trim()) {
      setError('Content is required.')
      return
    }
    
    try {
      await createMutation.mutateAsync({
        event: eventIdNum,
        title: title.trim(),
        content: finalContent,
        is_published: isPublished,
      })
      
      navigate({
        to: '/events/$eventId/event-content',
        params: { eventId },
        search: { created: 'true' },
      })
    } catch (err) {
      // Prefer the structured field error (request() attaches the parsed
      // DRF body as .data): the title-collision 400 carries an actionable
      // message — show it without the "400: title:" prefix noise.
      const data = (err as { data?: { title?: unknown } }).data
      const titleError = Array.isArray(data?.title) ? data.title[0] : data?.title
      setError(
        typeof titleError === 'string'
          ? titleError
          : err instanceof Error
            ? err.message
            : 'Failed to create content page'
      )
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Create Content Page</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Create a new content page for {event?.name}
          </p>
        </div>
        
        <Link
          to="/events/$eventId/event-content"
          params={{ eventId }}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 border border-neutral-300 dark:border-neutral-600 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Content Pages
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Content Page Details</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              placeholder="Enter page title..."
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              A descriptive title for your content page
            </p>
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content-editor" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Content *
            </label>
            <textarea
              id="content-editor"
              ref={editorRef}
              defaultValue={content}
              className="w-full min-h-[500px] px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Use the editor to create your content. You can upload images, add formatting, and more.
            </p>
          </div>

          {/* Published Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_published"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
            />
            <label htmlFor="is_published" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
              Publish immediately
            </label>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 -mt-4 ml-6">
            Uncheck to save as draft
          </p>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
            <Link
              to="/events/$eventId/event-content"
              params={{ eventId }}
              className="px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 border border-neutral-300 dark:border-neutral-600 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Content Page'}
            </button>
          </div>
        </form>
      </div>

      {/* Hide TinyMCE promotion */}
      <style>{`
        .tox-promotion,
        .tox-promotion-link {
          display: none !important;
        }
      `}</style>
    </div>
  )
}

// TinyMCE types
interface TinyMCE {
  remove: () => void
  init: (options: TinyMCEOptions) => void
  activeEditor?: TinyMCEEditor
}

interface TinyMCEOptions {
  selector: string
  height: number
  menubar: string
  plugins: string
  toolbar: string
  content_style: string
  skin: string
  content_css: string
  images_upload_url?: string
  automatic_uploads?: boolean
  images_file_types?: string
  file_picker_types?: string
  images_upload_handler?: (blobInfo: BlobInfo, progress: (percent: number) => void) => Promise<string>
  setup?: (editor: TinyMCEEditor) => void
}

interface TinyMCEEditor {
  getContent: () => string
  setContent: (content: string) => void
  on: (event: string, callback: () => void) => void
}

interface BlobInfo {
  blob: () => Blob
  filename: () => string
}
